/**
 * Vehicle Filter - Dynamic Make/Model/Year Cascading Dropdowns
 * With searchable dropdown UI
 */

(function() {
  'use strict';

  // Cache for vehicle data
  let vehicleDataCache = null;
  let vehicleDataPromise = null;

  /**
   * Fetch vehicle data from theme assets (with caching)
   */
  async function getVehicleData() {
    if (vehicleDataCache) {
      return vehicleDataCache;
    }

    if (vehicleDataPromise) {
      return vehicleDataPromise;
    }

    const container = document.querySelector('[data-vehicle-filter]');
    const assetUrl = container?.dataset?.vehicleDataUrl;

    if (!assetUrl) {
      throw new Error('Vehicle Filter: data-vehicle-data-url attribute not found');
    }

    vehicleDataPromise = fetch(assetUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load vehicle data: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        vehicleDataCache = data;
        return data;
      })
      .catch(error => {
        console.error('Vehicle Filter: Failed to load vehicle data', error);
        vehicleDataPromise = null;
        throw error;
      });

    return vehicleDataPromise;
  }

  /**
   * Create a searchable dropdown component
   */
  class SearchableDropdown {
    constructor(selectElement, options = {}) {
      this.select = selectElement;
      this.options = options;
      this.isOpen = false;
      this.items = [];
      this.filteredItems = [];
      this.selectedIndex = -1;
      this.placeholder = this.select.options[0]?.textContent || 'Select...';

      this.createDropdown();
      this.bindEvents();
    }

    createDropdown() {
      // Hide original select
      this.select.style.display = 'none';

      // Create wrapper
      this.wrapper = document.createElement('div');
      this.wrapper.className = 'searchable-dropdown';

      // Create trigger button
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'searchable-dropdown__trigger';
      this.trigger.innerHTML = `
        <span class="searchable-dropdown__value">${this.placeholder}</span>
        <button type="button" class="searchable-dropdown__clear" aria-label="Clear selection" style="display: none;">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <svg class="searchable-dropdown__arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      // Create dropdown panel
      this.panel = document.createElement('div');
      this.panel.className = 'searchable-dropdown__panel';
      this.panel.innerHTML = `
        <div class="searchable-dropdown__search">
          <input type="text" class="searchable-dropdown__input" placeholder="Type to search..." autocomplete="off">
        </div>
        <div class="searchable-dropdown__list"></div>
      `;

      this.wrapper.appendChild(this.trigger);
      this.wrapper.appendChild(this.panel);
      this.select.parentNode.insertBefore(this.wrapper, this.select.nextSibling);

      // Get references
      this.input = this.panel.querySelector('.searchable-dropdown__input');
      this.list = this.panel.querySelector('.searchable-dropdown__list');
      this.valueDisplay = this.trigger.querySelector('.searchable-dropdown__value');
      this.clearButton = this.trigger.querySelector('.searchable-dropdown__clear');
    }

    bindEvents() {
      // Clear button click
      this.clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.clear();
      });

      // Toggle dropdown
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        // Don't toggle if clicking on clear button
        if (e.target.closest('.searchable-dropdown__clear')) {
          return;
        }
        this.toggle();
      });

      // Search input
      this.input.addEventListener('input', () => {
        this.filter(this.input.value);
      });

      // Keyboard navigation
      this.input.addEventListener('keydown', (e) => {
        switch(e.key) {
          case 'ArrowDown':
            e.preventDefault();
            this.navigate(1);
            break;
          case 'ArrowUp':
            e.preventDefault();
            this.navigate(-1);
            break;
          case 'Enter':
            e.preventDefault();
            if (this.selectedIndex >= 0 && this.filteredItems[this.selectedIndex]) {
              this.selectItem(this.filteredItems[this.selectedIndex]);
            }
            break;
          case 'Escape':
            this.close();
            break;
        }
      });

      // Click outside to close
      document.addEventListener('click', (e) => {
        if (!this.wrapper.contains(e.target)) {
          this.close();
        }
      });

      // List item clicks
      this.list.addEventListener('click', (e) => {
        const item = e.target.closest('.searchable-dropdown__item');
        if (item) {
          const value = item.dataset.value;
          const itemData = this.items.find(i => i.value === value);
          if (itemData) {
            this.selectItem(itemData);
          }
        }
      });
    }

    setItems(items) {
      this.items = items;
      this.filteredItems = [...items];

      // Reset the displayed value when items change
      this.select.value = '';
      this.valueDisplay.textContent = this.placeholder;
      this.valueDisplay.classList.remove('has-value');

      // Also update the hidden select with options so value assignment works
      this.select.innerHTML = `<option value="">${this.placeholder}</option>`;
      items.forEach(item => {
        const option = document.createElement('option');
        option.value = item.value;
        option.textContent = item.label;
        this.select.appendChild(option);
      });

      this.renderList();
    }

    renderList() {
      this.list.innerHTML = this.filteredItems.map((item, index) => `
        <div class="searchable-dropdown__item ${index === this.selectedIndex ? 'is-highlighted' : ''}" data-value="${item.value}">
          ${item.label}
        </div>
      `).join('');

      if (this.filteredItems.length === 0) {
        this.list.innerHTML = '<div class="searchable-dropdown__empty">No results found</div>';
      }
    }

    filter(query) {
      const q = query.toLowerCase().trim();
      if (!q) {
        this.filteredItems = [...this.items];
      } else {
        this.filteredItems = this.items.filter(item =>
          item.label.toLowerCase().includes(q)
        );
      }
      this.selectedIndex = this.filteredItems.length > 0 ? 0 : -1;
      this.renderList();
    }

    navigate(direction) {
      if (this.filteredItems.length === 0) return;

      this.selectedIndex += direction;
      if (this.selectedIndex < 0) this.selectedIndex = this.filteredItems.length - 1;
      if (this.selectedIndex >= this.filteredItems.length) this.selectedIndex = 0;

      this.renderList();

      // Scroll into view
      const highlighted = this.list.querySelector('.is-highlighted');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }

    selectItem(item, skipChangeEvent = false) {
      this.select.value = item.value;
      this.valueDisplay.textContent = item.label;
      this.valueDisplay.classList.add('has-value');
      this.clearButton.style.display = 'flex';
      this.close();

      // Trigger change event on original select (unless skipped for pre-population)
      if (!skipChangeEvent) {
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    clear() {
      this.select.value = '';
      this.valueDisplay.textContent = this.placeholder;
      this.valueDisplay.classList.remove('has-value');
      this.clearButton.style.display = 'none';

      // Trigger change event to update dependent dropdowns
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    reset() {
      this.select.value = '';
      this.valueDisplay.textContent = this.placeholder;
      this.valueDisplay.classList.remove('has-value');
      this.clearButton.style.display = 'none';
      this.items = [];
      this.filteredItems = [];
      this.renderList();
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      if (this.items.length === 0) return;

      this.isOpen = true;
      this.wrapper.classList.add('is-open');
      this.input.value = '';
      this.filter('');

      // Only auto-focus on non-touch devices to prevent mobile keyboard from opening
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (!isTouchDevice) {
        setTimeout(() => this.input.focus(), 10);
      }
    }

    close() {
      this.isOpen = false;
      this.wrapper.classList.remove('is-open');
      this.selectedIndex = -1;
    }

    setDisabled(disabled) {
      this.trigger.disabled = disabled;
      this.wrapper.classList.toggle('is-disabled', disabled);
    }
  }

  /**
   * Parse URL tags from the current pathname
   * URL format: /collections/all/make_byd+model_sealion-6+year_2025+part_engine
   * @returns {{ make: string|null, model: string|null, year: string|null, part: string|null }|null}
   */
  function parseUrlTags() {
    const pathname = window.location.pathname;
    const segments = pathname.split('/');

    // The tags are in the last segment after /collections/{handle}/
    const lastSegment = segments[segments.length - 1];

    // Check if this segment contains tags (has underscores indicating tag format)
    if (!lastSegment || !lastSegment.includes('_')) {
      return null;
    }

    // Split by + to get individual tags
    const tags = lastSegment.split('+');
    const filterValues = { make: null, model: null, year: null, part: null };

    tags.forEach(tag => {
      // Split only on the first underscore (e.g., "make_alfa-romeo" -> ["make", "alfa-romeo"])
      const underscoreIndex = tag.indexOf('_');
      if (underscoreIndex > 0) {
        const key = tag.substring(0, underscoreIndex);
        const value = tag.substring(underscoreIndex + 1);
        if (key === 'make' || key === 'model' || key === 'year' || key === 'part') {
          filterValues[key] = value;
        }
      }
    });

    // Return if at least one tag is present (any combination is valid)
    if (!filterValues.make && !filterValues.model && !filterValues.year && !filterValues.part) {
      return null;
    }

    return filterValues;
  }

  /**
   * Initialize a vehicle filter instance
   */
  function initVehicleFilter(container) {
    const makeSelect = container.querySelector('[data-vehicle-make]');
    const modelSelect = container.querySelector('[data-vehicle-model]');
    const yearSelect = container.querySelector('[data-vehicle-year]');
    const categorySelect = container.querySelector('[data-vehicle-category]');
    const form = container.querySelector('form');

    if (!makeSelect || !modelSelect || !yearSelect || !categorySelect) {
      console.warn('Vehicle Filter: Missing required select elements', container);
      return;
    }

    // Create searchable dropdowns
    const makeDropdown = new SearchableDropdown(makeSelect);
    const modelDropdown = new SearchableDropdown(modelSelect);
    const yearDropdown = new SearchableDropdown(yearSelect);
    const categoryDropdown = new SearchableDropdown(categorySelect);

    // Disable make dropdown until data loads
    makeDropdown.setDisabled(true);
    // Disable category dropdown until data loads
    categoryDropdown.setDisabled(true);

    let currentMake = null;
    let isSearching = false;

    // Parse URL tags for pre-population
    const urlTags = parseUrlTags();

    /**
     * Populate make and category dropdowns
     */
    async function populateMakes() {
      try {
        const data = await getVehicleData();

        const items = data.makes.map(make => ({
          value: make.value,
          label: make.label
        }));

        makeDropdown.setItems(items);
        makeDropdown.setDisabled(false);
        modelDropdown.reset();
        yearDropdown.reset();

        // Populate categories (independent dropdown)
        if (data.categories && data.categories.length > 0) {
          const categoryItems = data.categories.map(category => ({
            value: category.value,
            label: category.label
          }));
          categoryDropdown.setItems(categoryItems);
          categoryDropdown.setDisabled(false);

          // Pre-populate category from URL tags if available
          if (urlTags && urlTags.part) {
            const categoryItem = categoryItems.find(item => item.value === urlTags.part);
            if (categoryItem) {
              categoryDropdown.selectItem(categoryItem, true);
            }
          }
        }

        // Pre-populate from URL tags if available (skip change events to avoid race conditions)
        if (urlTags && urlTags.make) {
          const makeItem = items.find(item => item.value === urlTags.make);
          if (makeItem) {
            makeDropdown.selectItem(makeItem, true);
            // Wait for models to load, then pre-select model
            await prePopulateModel(data, urlTags.make);
          }
        }

      } catch (error) {
        console.error('Vehicle Filter: Error populating makes', error);
      }
    }

    /**
     * Pre-populate model from URL tags
     */
    async function prePopulateModel(data, makeValue) {
      if (!urlTags || !urlTags.model) return;

      const make = data.makes.find(m => m.value === makeValue);
      if (!make) return;

      const modelItems = make.models.map(model => ({
        value: model.value,
        label: model.label
      }));

      const modelItem = modelItems.find(item => item.value === urlTags.model);
      if (modelItem) {
        // Need to set items first so selectItem works
        modelDropdown.setItems(modelItems);
        modelDropdown.selectItem(modelItem, true);
        currentMake = makeValue;

        // Pre-populate year if available
        await prePopulateYear(data, makeValue, urlTags.model);
      }
    }

    /**
     * Pre-populate year from URL tags
     */
    async function prePopulateYear(data, makeValue, modelValue) {
      if (!urlTags || !urlTags.year) return;

      const make = data.makes.find(m => m.value === makeValue);
      const model = make?.models.find(m => m.value === modelValue);

      if (!model || !model.years) return;

      const yearItems = model.years.map(year => ({
        value: String(year),
        label: String(year)
      }));

      const yearItem = yearItems.find(item => item.value === urlTags.year);
      if (yearItem) {
        yearDropdown.setItems(yearItems);
        yearDropdown.selectItem(yearItem, true);
      }
    }

    /**
     * Update models based on selected make
     */
    async function updateModels(makeValue) {
      currentMake = makeValue;

      if (!makeValue) {
        modelDropdown.reset();
        yearDropdown.reset();
        return;
      }

      try {
        const data = await getVehicleData();
        const make = data.makes.find(m => m.value === makeValue);

        if (!make) {
          modelDropdown.reset();
          yearDropdown.reset();
          return;
        }

        const items = make.models.map(model => ({
          value: model.value,
          label: model.label
        }));

        modelDropdown.setItems(items);
        yearDropdown.reset();

        // Auto-open model dropdown after selecting make
        setTimeout(() => modelDropdown.open(), 50);

      } catch (error) {
        console.error('Vehicle Filter: Error updating models', error);
      }
    }

    /**
     * Update years based on selected model
     */
    async function updateYears(modelValue) {
      if (!currentMake || !modelValue) {
        yearDropdown.reset();
        return;
      }

      try {
        const data = await getVehicleData();
        const make = data.makes.find(m => m.value === currentMake);
        const model = make?.models.find(m => m.value === modelValue);

        if (!model || !model.years || model.years.length === 0) {
          yearDropdown.reset();
          return;
        }

        const items = model.years.map(year => ({
          value: String(year),
          label: String(year)
        }));

        yearDropdown.setItems(items);

        // Auto-open year dropdown after selecting model
        setTimeout(() => yearDropdown.open(), 50);

      } catch (error) {
        console.error('Vehicle Filter: Error updating years', error);
      }
    }

    /**
     * Auto-open category dropdown after year selection
     */
    function handleYearSelection() {
      // Auto-open category dropdown after selecting year
      setTimeout(() => categoryDropdown.open(), 50);
    }

    /**
     * Handle form submission - build tag-filtered URL and fetch via AJAX
     */
    function handleSubmit(event) {
      console.log('Vehicle Filter: handleSubmit called');
      event.preventDefault();
      event.stopPropagation();

      // Prevent duplicate requests
      if (isSearching) {
        console.log('Vehicle Filter: Search already in progress, ignoring duplicate request');
        return;
      }

      isSearching = true;

      const makeValue = makeSelect.value;
      const modelValue = modelSelect.value;
      const yearValue = yearSelect.value;
      const categoryValue = categorySelect.value;

      const tags = [];
      if (makeValue) tags.push(`make_${makeValue}`);
      if (modelValue) tags.push(`model_${modelValue}`);
      if (yearValue) tags.push(`year_${yearValue}`);
      if (categoryValue) tags.push(`part_${categoryValue}`);

      if (tags.length === 0) {
        isSearching = false;
        return;
      }

      const baseUrl = form.getAttribute('action') || '/collections/all';
      const tagString = tags.join('+');
      const filterUrl = `${baseUrl}/${tagString}`;

      // Update URL without reload
      history.pushState({}, '', filterUrl);

      // Show loading state on results area
      const resultsContainer = document.querySelector('#ResultsList');
      if (resultsContainer) {
        resultsContainer.classList.add('is-loading');
      }

      // Fetch the filtered collection page via AJAX
      fetch(filterUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.text())
      .then(html => {
        // Parse the response HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find the product grid container in the new content
        const newResultsList = doc.querySelector('#ResultsList');
        const currentResultsList = document.querySelector('#ResultsList');

        if (newResultsList && currentResultsList) {
          // Replace the content
          currentResultsList.innerHTML = newResultsList.innerHTML;
        } else {
          // Fallback to regular navigation if parsing fails
          window.location.href = filterUrl;
        }
      })
      .catch(error => {
        console.error('Error fetching filtered results:', error);
        // Remove loading state before fallback navigation
        if (resultsContainer) {
          resultsContainer.classList.remove('is-loading');
        }
        // Fallback to regular navigation on error
        window.location.href = filterUrl;
      })
      .finally(() => {
        // Reset searching flag and remove loading state
        isSearching = false;
        if (resultsContainer) {
          resultsContainer.classList.remove('is-loading');
        }
      });
    }

    // Event listeners - use select.value directly since e.target may not have value property
    makeSelect.addEventListener('change', () => updateModels(makeSelect.value));
    modelSelect.addEventListener('change', () => updateYears(modelSelect.value));
    yearSelect.addEventListener('change', () => {
      if (yearSelect.value) {
        handleYearSelection();
      }
    });

    if (form) {
      // Listen for the custom vehicleFilterSubmit event
      form.addEventListener('vehicleFilterSubmit', handleSubmit);
      console.log('Vehicle Filter: Form submit handler attached');
    }

    // Reset button handler
    const resetButton = container.querySelector('[data-vehicle-reset]');
    if (resetButton) {
      resetButton.addEventListener('click', () => {
        // Clear all dropdowns using .clear() method which keeps items intact
        makeDropdown.clear();
        modelDropdown.clear();
        yearDropdown.clear();
        categoryDropdown.clear();

        // Fetch unfiltered collection via AJAX
        const baseUrl = form.getAttribute('action') || '/collections/all';

        // Update URL without reload
        history.pushState({}, '', baseUrl);

        // Show loading state on results area
        const resultsContainer = document.querySelector('#ResultsList');
        if (resultsContainer) {
          resultsContainer.classList.add('is-loading');
        }

        // Fetch the unfiltered collection page via AJAX
        fetch(baseUrl, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .then(response => response.text())
        .then(html => {
          // Parse the response HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Find the product grid container in the new content
          const newResultsList = doc.querySelector('#ResultsList');
          const currentResultsList = document.querySelector('#ResultsList');

          if (newResultsList && currentResultsList) {
            // Replace the content
            currentResultsList.innerHTML = newResultsList.innerHTML;
          } else {
            // Fallback to regular navigation if parsing fails
            window.location.href = baseUrl;
          }
        })
        .catch(error => {
          console.error('Error fetching unfiltered results:', error);
          // Fallback to regular navigation on error
          window.location.href = baseUrl;
        })
        .finally(() => {
          // Remove loading state
          if (resultsContainer) {
            resultsContainer.classList.remove('is-loading');
          }
        });
      });
    }

    // Initialize
    populateMakes();
  }

  /**
   * Initialize all vehicle filters on the page
   */
  function initAllFilters() {
    const containers = document.querySelectorAll('[data-vehicle-filter]');
    containers.forEach(initVehicleFilter);
  }

  // Add global form submit handler to catch all vehicle filter forms
  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (!form.closest('[data-vehicle-filter]')) return;

    console.log('Vehicle Filter: Global submit handler caught form submission');
    e.preventDefault();
    e.stopPropagation();

    // Trigger the form's submit event which will be handled by initVehicleFilter
    // But since we already prevented default, we need to manually call the handler
    const submitEvent = new Event('vehicleFilterSubmit', { bubbles: true, cancelable: true });
    form.dispatchEvent(submitEvent);
  }, true); // Use capture phase to intercept early

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllFilters);
  } else {
    initAllFilters();
  }

  // Expose for manual initialization
  window.VehicleFilter = {
    init: initVehicleFilter,
    initAll: initAllFilters,
    getData: getVehicleData
  };

})();
